import { Fragment, type ReactElement, type ReactNode } from "react";

import { operatorOptionMap } from "app/pages/sales-intelligence/types/commonMaps";

import {
    StyledConditionCardActions,
    StyledConditionCardContainer,
    StyledConditionCardContent,
    StyledConditionCardFieldLabel,
    StyledConditionCardHeader,
    StyledConditionCardIconButton,
    StyledConditionCardText,
    StyledConditionCardTitle,
    StyledConditionCardValueDivider,
} from "./styles";

export type ConditionCardProps = {
    /** Inclusion header (e.g. "Include" / "Exclude"). Consumers resolve their own label/translatable. */
    readonly inclusionLabel: ReactNode;
    /** Optional field name shown under the header (e.g. "Account owner"). Omitted when a card has no field row. */
    readonly fieldLabel?: ReactNode;
    /** The condition's values, rendered bold and joined by "OR". */
    readonly values: readonly ReactNode[];
    readonly hideActions?: boolean;
    readonly onEdit: () => void;
    readonly onDelete: () => void;
    /** Renders full-width (compound filter cards) instead of the fixed chip width (Technologies). */
    readonly fullWidth?: boolean;
    readonly dataAutomation?: string;
};

/**
 * Generic condition card: an inclusion header with edit/remove actions, an optional field name, and the
 * condition's values joined by "OR". Shared by the Technologies filter and the compound (per-condition) group
 * filter so both render an identical card.
 */
export const ConditionCard = ({
    inclusionLabel,
    fieldLabel,
    values,
    hideActions = false,
    onEdit,
    onDelete,
    fullWidth = false,
    dataAutomation,
}: ConditionCardProps): ReactElement => (
    <StyledConditionCardContainer
        $fullWidth={fullWidth}
        data-automation={dataAutomation}
    >
        <StyledConditionCardHeader>
            <StyledConditionCardTitle>{inclusionLabel}</StyledConditionCardTitle>

            {hideActions ? null : (
                <StyledConditionCardActions>
                    <StyledConditionCardIconButton
                        iconName="edit-icon"
                        iconSize="xs"
                        type="flat"
                        onClick={onEdit}
                        dataAutomation="condition-card-edit"
                    />
                    <StyledConditionCardIconButton
                        iconName="clear-circle"
                        iconSize="xs"
                        type="flat"
                        onClick={onDelete}
                        dataAutomation="condition-card-delete"
                    />
                </StyledConditionCardActions>
            )}
        </StyledConditionCardHeader>

        {fieldLabel ? <StyledConditionCardFieldLabel>{fieldLabel}</StyledConditionCardFieldLabel> : null}

        <StyledConditionCardContent>
            {values.map((value, index) => (
                <Fragment key={index}>
                    {index > 0 ? (
                        <StyledConditionCardValueDivider>{operatorOptionMap.or}</StyledConditionCardValueDivider>
                    ) : null}
                    <StyledConditionCardText>{value}</StyledConditionCardText>
                </Fragment>
            ))}
        </StyledConditionCardContent>
    </StyledConditionCardContainer>
);
